import React, { useState, useEffect } from 'react';

import Button from 'react-bootstrap/Button';
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Typeahead } from 'react-bootstrap-typeahead';

import { secondsToTime } from '../../utilities/scoringUtilities';
import flagService from '../../services/FlagService';
import { ScoringInputType } from '../../models/enums/ScoringInputType';
import { FlagInputTypes } from '../../models/enums/FlagInputTypes';

const getLikelihood = (value) => {
    if (value === "Almost Certain") { return 95 }
    else if (value === "None") { return 0 }
    else if (value === "Very Unlikely") { return 15 }
    else if (value === "Unlikely") { return 30 }
    else if (value === "50%") { return 50 }
    else if (value === "Likely") { return 65 }
    else if (value === "Very Likely") { return 80 }
    else { return 0 }
}

const getSelectedType = (types, value) => {
    if (value === 0) { return types[0] }
    else if (value === 15) { return types[1] }
    else if (value === 30) { return types[2] }
    else if (value === 50) { return types[3] }
    else if (value === 65) { return types[4] }
    else if (value === 80) { return types[5] }
    else if (value === 95) { return types[6] }
    else { return types[0] }
}

const FlagModal = (props) => {
    const { show, onHide, inputItem, fixtureRoomId } = props;
    const types = ['None', 'Very Unlikely', 'Unlikely', '50%', 'Likely', 'Very Likely', 'Almost Certain'];
    const typaheadRef = React.createRef();
    const refereeDecisionRef = React.createRef();
    const refereeOffenceRef = React.createRef();
    const [flagReasonsList, setFlagReasonsList] = useState(null);
    const [refereeDecisionList, setRefereeDecision] = useState(null);
    const [refereeOffenceList, setRefereeOffence] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState(null);
    const [selectedRefereeDecision, setSelectedRefereeDecision] = useState(null);
    const [selectedRefereeOffence, setSelectedRefereeOffence] = useState(null);
    const [notes, setNotes] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [homePenaltyValue, setHomePenaltyValue] = useState(0);
    const [awayPenaltyValue, setAwayPenaltyValue] = useState(0);
    const [playContinuesValue, setPlayContinuesValue] = useState(0);
    const [homeLikelihood, setSelectedHomeLikelihood] = useState(types[0]);
    const [awayLikelihood, setSelectedAwayLikelihood] = useState(types[0]);

    const refDecision = selectedOptions?.find((element) => {
        return element.label === 'Ref Decision';
    })

    useEffect(() => {
        if (!flagReasonsList) {
            flagService.getFlagReasonsList().then((flagReasons) => {
                setFlagReasonsList(flagReasons);
            });
        }
    }, [flagReasonsList]);

    useEffect(() => {
        if (!flagReasonsList) {
            flagService.getRefereeDecision().then((refereeDecisionDetails) => {
                setRefereeDecision(refereeDecisionDetails);
            });
        }
    }, [flagReasonsList]);

    useEffect(() => {
        if (!flagReasonsList) {
            flagService.getRefereeOffence().then((refereeOffenceDetails) => {
                setRefereeOffence(refereeOffenceDetails);
            });
        }
    }, [flagReasonsList]);

    useEffect(() => {
        if (inputItem && inputItem.isFlagged) {
            const getFlagDetails = async () => {
                const flagDetails = await flagService.getFlagDetails(inputItem.flagId);
                setSelectedOptions(flagDetails.options.map(x => {
                    return {
                        id: x,
                        label: flagReasonsList.find(y => y.id == x).text,
                    };
                }));
                setNotes(flagDetails.notes);
                setSelectedRefereeDecision(
                    [
                        {
                            id: flagDetails.refereeDecisions,
                            label: refereeDecisionList.find(y => y.id === flagDetails.refereeDecisions)?.text
                        },
                    ]
                )
                setSelectedRefereeOffence(
                    [
                        {
                            id: flagDetails.refereeOffences,
                            label: refereeOffenceList.find(y => y.id === flagDetails.refereeOffences)?.text
                        },
                    ]
                )
                setHomePenaltyValue(parseInt(flagDetails.homePenalty))
                setAwayPenaltyValue(parseInt(flagDetails.awayPenalty))
                setPlayContinuesValue(parseInt(flagDetails.playContinues))
                setSelectedHomeLikelihood(getSelectedType(types, parseInt(flagDetails.homePenalty)))
                setSelectedAwayLikelihood(getSelectedType(types, parseInt(flagDetails.awayPenalty)))
            };
            getFlagDetails();
        }
        setErrorMessage(null);
    }, [inputItem]);

    useEffect(() => {
        setPlayContinuesValue(100 - homePenaltyValue - awayPenaltyValue)
    }, [homePenaltyValue, awayPenaltyValue, playContinuesValue])

    let errorLabel =
        errorMessage && errorMessage.length > 0 ?
            <span className="text-danger">{errorMessage}</span>
            : <></>

    const typaheadReasons = flagReasonsList?.map((x) => {
        return { id: x.id, label: x.text }
    });

    const refereeDecisions = refereeDecisionList?.map((x) => {
        return { id: x.id, label: x.text }
    });

    const refereeOffences = refereeOffenceList?.map((x) => {
        return { id: x.id, label: x.text }
    });

    let handleNotesChange = (event) => {
        setNotes(event.target.value);
    };

    const handleRefereeLikelihood = async (isHome, type) => {
        if (isHome) {
            setSelectedHomeLikelihood(type)
            setHomePenaltyValue(getLikelihood(type))
            setPlayContinuesValue(100 - homePenaltyValue - awayPenaltyValue)
        } else {
            setSelectedAwayLikelihood(type)
            setAwayPenaltyValue(getLikelihood(type))
            setPlayContinuesValue(100 - homePenaltyValue - awayPenaltyValue)
        }
    }

    const handleCancel = () => {
        setNotes(null);
        setSelectedRefereeDecision(null);
        setSelectedRefereeOffence(null);
        setSelectedOptions([]);
        setSelectedAwayLikelihood(types[0])
        setSelectedHomeLikelihood(types[0])
        setPlayContinuesValue(0)
        setHomePenaltyValue(0)
        setAwayPenaltyValue(0)
    }

    const handleClose = () => {
        setNotes(null);
        setSelectedRefereeDecision(null);
        setSelectedRefereeOffence(null);
        setSelectedOptions([]);
        onHide();
        setSelectedAwayLikelihood(types[0])
        setSelectedHomeLikelihood(types[0])
        setHomePenaltyValue(0)
        setAwayPenaltyValue(0)
        setPlayContinuesValue(0)
    }

    let removeFlagButton = inputItem && inputItem.isFlagged ? (
        <Button
            type="button"
            variant="primary"
            disabled={false}
            className="mr-2"
            onClick={async (e) => {
                handleCancel();
                await flagService.deleteFlag(inputItem.flagId).then((res) => {
                    handleClose()
                }).catch(e => {
                    console.log(e);
                });
            }}
        >
            Remove Flag
        </Button>
    ) : (
        <></>
    );

    let refButtonUI = (isHome) => {
        return (
            <div>
                {types.map(type => (
                    <Button key={type} disabled={!isHome ? awayLikelihood === type : homeLikelihood === type} onClick={async (e) => { handleRefereeLikelihood(isHome, type) }} style={{ margin: '2.5px' }}>{type}</Button>
                ))}
            </div>
        )
    }

    return <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        centered
    >
        <Modal.Header closeButton>
            <Modal.Title>
                <h3>
                    Flag
                </h3>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Row>
                <Col>
                    <div>
                        {inputItem && <>
                            <span className="py-1"> <b>Time: </b></span>
                            <span className="py-1">{secondsToTime(inputItem.time)}</span>
                        </>}
                    </div>
                    <Row>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div>
                        <Form.Group>
                            <Form.Label><b>Type: </b></Form.Label>
                            {flagReasonsList &&
                                <Typeahead
                                    id="basic-typeahead-multiple"
                                    ref={typaheadRef}
                                    clearButton
                                    multiple
                                    onChange={(selected) => {
                                        setSelectedOptions(selected);
                                    }}
                                    options={typaheadReasons}
                                    selected={selectedOptions}
                                    placeholder="Choose Type"
                                />
                            }
                        </Form.Group>
                    </div>
                    {refDecision && <div>
                        <div style={{ marginBottom: '10px' }}>
                            <Form.Group>
                                {refereeDecisionList &&
                                    <Typeahead
                                        id="basic-typeahead-referee-decision"
                                        ref={refereeDecisionRef}
                                        clearButton
                                        onChange={(selected) => {
                                            setSelectedRefereeDecision(selected);
                                        }}
                                        options={refereeDecisions}
                                        selected={selectedRefereeDecision}
                                        placeholder="Choose Decision"
                                    />
                                }
                            </Form.Group>
                        </div>
                        <div>
                            <Form.Group>
                                {refereeOffenceList &&
                                    <Typeahead
                                        id="basic-typeahead-referee-offence"
                                        ref={refereeOffenceRef}
                                        clearButton
                                        onChange={(selected) => {
                                            setSelectedRefereeOffence(selected);
                                        }}
                                        options={refereeOffences}
                                        selected={selectedRefereeOffence}
                                        placeholder="Choose Offence"
                                    />
                                }
                            </Form.Group>
                        </div>
                        <div>
                            <label><b>Likelihood:</b></label>
                            <div>
                                <p><b>Home Penalty</b></p>
                                {refButtonUI(true)}
                            </div>
                            <div>
                                <p><b>Away Penalty</b></p>
                                {refButtonUI(false)}
                            </div>
                        </div>
                    </div>}
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="form-group">
                        <label htmlFor="NotesInput"> <b>Notes: </b> </label>
                        <Form.Control
                            id="NotesInput"
                            as="textarea"
                            rows="5"
                            value={notes ? notes : ""}
                            onChange={handleNotesChange}
                            placeholder="Type in your Notes"
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>{errorLabel}</Col>
            </Row>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="danger" onClick={(e) => {
                handleCancel();
                setErrorMessage(null);
                onHide();
            }}>Cancel</Button>

            {removeFlagButton}

            <Button variant="success" onClick={async (e) => {
                let flagType = null;
                let refereeDecision = (selectedRefereeDecision != null) ? selectedRefereeDecision[0].id : 0
                let refereeOffence = (selectedRefereeDecision != null) ? selectedRefereeOffence[0].id : 0
                const penaltyValidator = refDecision ? playContinuesValue >= 0 ? true : false : true
                if (inputItem.type === ScoringInputType.ClipStart) {
                    flagType = FlagInputTypes.ClipStart;
                } else if (inputItem.type === ScoringInputType.ClipEnd) {
                    flagType = FlagInputTypes.ClipEnd
                } else if (inputItem.type === ScoringInputType.YellowCard || inputItem.type === ScoringInputType.RedCard) {
                    flagType = FlagInputTypes.Card;
                } else if (inputItem.type === ScoringInputType.YellowCardReturned) {
                    flagType = FlagInputTypes.CardReturn;
                } else if (inputItem.type == ScoringInputType.RedCardReturned) {
                    flagType = FlagInputTypes.CardReturn;
                } else {
                    flagType = FlagInputTypes.Score
                }

                try {
                    if (inputItem && inputItem.isFlagged && penaltyValidator) {
                        await flagService.updateFlag(inputItem.flagId, fixtureRoomId, notes, selectedOptions.map(x => x.id), parseInt(refereeDecision),
                            parseInt(refereeOffence), parseInt(homePenaltyValue), parseInt(awayPenaltyValue), playContinuesValue);
                        handleClose();
                    }
                    else if (penaltyValidator) {
                        await flagService.addFlag(fixtureRoomId, inputItem.id, inputItem.time, notes, flagType, selectedOptions.map(x => x.id), parseInt(refereeDecision),
                            parseInt(refereeOffence), parseInt(homePenaltyValue), parseInt(awayPenaltyValue), playContinuesValue);
                        handleClose();
                    }
                    else {
                        setErrorMessage('The Sum of Home and Away penalties should not be greater than 1. Please readjust the values.')
                    }
                } catch (err) {
                    console.log(err)
                    setErrorMessage("An Error Has Occured");
                }
            }}>Confirm</Button>
        </Modal.Footer>
    </Modal>;
}
export default FlagModal; 